import client from '@config/client'
import API_URL from '@config/services'
import { ID } from '../types'
import {
  ApiClientResponse,
  LoginPayload,
  LoginResponse,
  SignUpPayload,
  ActivationPayload,
  ResetPasswordPayload,
  ResetPasswordConfirmPayload,
  SetPasswordPayload,
  Profile,
  Organization,
  OrgUser,
  PermissionsMap
} from '../types/api'

interface TokenPayload {
  token: string
}

type ApiResponse<T = any> = Promise<{
  data?: T
  status?: number
  [key: string]: any
}>

/**
 * Gets the current user's organization
 * @returns Organization data
 */
export const getMyOrganization = (): ApiResponse<Organization> =>
  client
    .get(API_URL.PROFILES.GET_PROFILES_PROFILE)
    .then(response => response.data)
    .catch(error => error.response)

/**
 * Updates the current user's organization
 * @param payload - Organization data to update
 * @returns API response
 */
export const patchMyOrganization = (
  payload: Partial<Organization>
): ApiResponse =>
  client
    .patch(API_URL.PROFILES.GET_PROFILES_PROFILE, payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Authenticates a user
 * @param payload - Login credentials
 * @returns API response with access token and user data
 */
export const postLogin = (payload: LoginPayload): ApiResponse => {
  return client
    .post(API_URL.AUTH.LOGIN, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Refreshes an authentication token
 * @param payload - Refresh token
 * @returns API response with new access token
 */
export const onRefreshToken = (payload: string): ApiResponse => {
  return client
    .post(API_URL.AUTH.TOKEN_REFRESH, {
      token: payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Registers a new user
 * @param payload - User registration data
 * @returns API response
 */
export const postSignUp = (payload: SignUpPayload): ApiResponse => {
  return client
    .post(API_URL.AUTH.SIGNUP, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Verifies a token is valid
 * @param payload - Token to verify
 * @returns API response
 */
export const postTokenVerify = (payload: TokenPayload): ApiResponse => {
  return client
    .post(API_URL.AUTH.TOKEN_VERIFY, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Activates a user account
 * @param payload - Activation data
 * @returns API response
 */
export const postActivation = (payload: ActivationPayload): ApiResponse => {
  return client
    .post(API_URL.AUTH.ACTIVATION, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Gets the current user's profile
 * @returns API response with user profile
 */
export const getMyProfile = (): ApiResponse<Profile> => {
  return client
    .get(API_URL.AUTH.ME)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Gets the current user's organization user record
 * @returns API response with OrgUser data
 */
export const getOrgUser = (): ApiResponse => {
  return client
    .get(API_URL.ORG_USER.GET_MY_ORG_USER())
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Gets the current user's permissions
 * @param orguser - Optional organization user ID to view permissions as
 * @returns Permissions map
 */
export const getMyPermissions = (orguser?: string): ApiResponse<string[]> => {
  let url = API_URL.PERMISSIONS.GET_PERMISSIONS_MAP
  if (orguser) {
    url += '?view_as=' + orguser
  }
  return client
    .get(url)
    .then(response => response.data)
    .catch(error => error.response)
}

/**
 * Gets a new refresh token
 * @param payload - Current token
 * @returns API response
 */
export const getRefreshToken = (payload: string): ApiResponse => {
  return client
    .post(API_URL.AUTH.ACTIVATION, { token: payload })
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Resends a verification email
 * @param payload - Email to send verification to
 * @returns API response
 */
export const postResendVerification = (payload: {
  email: string
}): ApiResponse => {
  return client
    .post(API_URL.AUTH.RESEND_VERIFICATION, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Sets a new password
 * @param payload - Password data
 * @returns API response
 */
export const postSetPassword = (payload: SetPasswordPayload): ApiResponse => {
  return client
    .post(API_URL.AUTH.SET_PASSWORD, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Initiates a password reset
 * @param payload - Email to send reset to
 * @returns API response
 */
export const postResetPassword = (
  payload: ResetPasswordPayload
): ApiResponse => {
  return client
    .post(API_URL.AUTH.RESET_PASSWORD, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Confirms a password reset
 * @param payload - Reset confirmation data
 * @returns API response
 */
export const postResetPasswordConfirm = (
  payload: ResetPasswordConfirmPayload
): ApiResponse => {
  return client
    .post(API_URL.AUTH.RESET_PASSWORD_CONFIRM, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Gets all organizations the user has access to
 * @returns API response with organizations list
 */
export const getAllOrganizations = (): ApiResponse<Organization[]> => {
  return client
    .get(API_URL.ORG_USER.GET_ORGANIZATIONS)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates parts of the current user's profile
 * @param payload - Profile data to update
 * @returns API response
 */
export const updateMyProfilePartial = (
  payload: Partial<Profile>
): ApiResponse => {
  return client
    .patch(API_URL.AUTH.ME, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Replaces the current user's profile
 * @param payload - Complete profile data
 * @returns API response
 */
export const updateMyProfile = (payload: Profile): ApiResponse => {
  return client
    .put(API_URL.AUTH.ME, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates parts of a user's profile
 * @param id - User ID
 * @param payload - Profile data to update
 * @returns API response
 */
export const updateUserProfilePartial = (
  id: ID,
  payload: Partial<Profile>
): ApiResponse => {
  return client
    .patch(API_URL.AUTH.PATCH_USER(id), payload)
    .then(response => response)
    .catch(error => error.response)
}
